import { Suspense, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BallStickViewer, MOLECULES } from "./BallStickViewer";

type Atom = { id: string; el: string; x: number; y: number };
type Bond = { from: string; to: string; order?: 1 | 2 | 3; wedge?: "up" | "down" };

type IsomerStructure = {
  label: string;
  name: string;
  note?: string;
  atoms: Atom[];
  bonds: Bond[];
  width?: number;
  height?: number;
  molecule3dKey?: keyof typeof MOLECULES;
};

type IsomerSet = {
  id: string;
  category: "structural" | "stereo";
  subtype: string;
  formula: string;
  title: string;
  description: string;
  structures: IsomerStructure[];
};

const isomerSets: IsomerSet[] = [
  {
    id: "c4h10",
    category: "structural",
    subtype: "Chain isomers",
    formula: "C₄H₁₀",
    title: "Butane vs. 2-methylpropane",
    description:
      "Same molecular formula, different carbon skeleton - a straight chain vs. a branched chain.",
    structures: [
      {
        label: "n-Butane",
        name: "Straight chain",
        atoms: [
          { id: "c1", el: "CH₃", x: 40, y: 80 },
          { id: "c2", el: "CH₂", x: 110, y: 80 },
          { id: "c3", el: "CH₂", x: 180, y: 80 },
          { id: "c4", el: "CH₃", x: 250, y: 80 },
        ],
        bonds: [
          { from: "c1", to: "c2" },
          { from: "c2", to: "c3" },
          { from: "c3", to: "c4" },
        ],
      },
      {
        label: "2-Methylpropane",
        name: "Branched",
        atoms: [
          { id: "c1", el: "CH₃", x: 40, y: 100 },
          { id: "c2", el: "CH", x: 120, y: 100 },
          { id: "c3", el: "CH₃", x: 200, y: 100 },
          { id: "c4", el: "CH₃", x: 120, y: 30 },
        ],
        bonds: [
          { from: "c1", to: "c2" },
          { from: "c2", to: "c3" },
          { from: "c2", to: "c4" },
        ],
      },
    ],
  },
  {
    id: "c2h6o",
    category: "structural",
    subtype: "Functional group isomers",
    formula: "C₂H₆O",
    title: "Ethanol vs. dimethyl ether",
    description:
      "Same atoms, different functional groups - an alcohol (–OH) vs. an ether (–O–).",
    structures: [
      {
        label: "Ethanol",
        name: "Alcohol",
        atoms: [
          { id: "c1", el: "CH₃", x: 40, y: 80 },
          { id: "c2", el: "CH₂", x: 120, y: 80 },
          { id: "o", el: "OH", x: 200, y: 80 },
        ],
        bonds: [
          { from: "c1", to: "c2" },
          { from: "c2", to: "o" },
        ],
      },
      {
        label: "Dimethyl ether",
        name: "Ether",
        atoms: [
          { id: "c1", el: "CH₃", x: 40, y: 80 },
          { id: "o", el: "O", x: 130, y: 80 },
          { id: "c2", el: "CH₃", x: 220, y: 80 },
        ],
        bonds: [
          { from: "c1", to: "o" },
          { from: "o", to: "c2" },
        ],
      },
    ],
  },
  {
    id: "c3h8o",
    category: "structural",
    subtype: "Position isomers",
    formula: "C₃H₈O",
    title: "Propan-1-ol vs. propan-2-ol",
    description:
      "Same skeleton and functional group, but the –OH is on a different carbon.",
    structures: [
      {
        label: "Propan-1-ol",
        name: "–OH on C1",
        atoms: [
          { id: "c1", el: "CH₃", x: 40, y: 80 },
          { id: "c2", el: "CH₂", x: 110, y: 80 },
          { id: "c3", el: "CH₂", x: 180, y: 80 },
          { id: "o", el: "OH", x: 250, y: 80 },
        ],
        bonds: [
          { from: "c1", to: "c2" },
          { from: "c2", to: "c3" },
          { from: "c3", to: "o" },
        ],
      },
      {
        label: "Propan-2-ol",
        name: "–OH on C2",
        atoms: [
          { id: "c1", el: "CH₃", x: 40, y: 100 },
          { id: "c2", el: "CH", x: 120, y: 100 },
          { id: "c3", el: "CH₃", x: 200, y: 100 },
          { id: "o", el: "OH", x: 120, y: 30 },
        ],
        bonds: [
          { from: "c1", to: "c2" },
          { from: "c2", to: "c3" },
          { from: "c2", to: "o" },
        ],
      },
    ],
  },
  {
    id: "c4h8",
    category: "stereo",
    subtype: "Cis–trans (geometric) isomers",
    formula: "C₄H₈",
    title: "cis- vs. trans-2-butene",
    description:
      "Restricted rotation around the C=C double bond gives two distinct geometric arrangements.",
    structures: [
      {
        label: "cis-2-Butene",
        name: "Same side",
        molecule3dKey: "cis-2-butene",
        atoms: [
          { id: "m1", el: "CH₃", x: 40, y: 30 },
          { id: "c1", el: "C", x: 110, y: 80 },
          { id: "c2", el: "C", x: 190, y: 80 },
          { id: "m2", el: "CH₃", x: 260, y: 30 },
          { id: "h1", el: "H", x: 110, y: 140 },
          { id: "h2", el: "H", x: 190, y: 140 },
        ],
        bonds: [
          { from: "m1", to: "c1" },
          { from: "c1", to: "c2", order: 2 },
          { from: "c2", to: "m2" },
          { from: "c1", to: "h1" },
          { from: "c2", to: "h2" },
        ],
      },
      {
        label: "trans-2-Butene",
        name: "Opposite sides",
        molecule3dKey: "trans-2-butene",
        atoms: [
          { id: "m1", el: "CH₃", x: 40, y: 30 },
          { id: "c1", el: "C", x: 110, y: 80 },
          { id: "c2", el: "C", x: 190, y: 80 },
          { id: "h2", el: "H", x: 260, y: 30 },
          { id: "h1", el: "H", x: 110, y: 140 },
          { id: "m2", el: "CH₃", x: 190, y: 140 },
        ],
        bonds: [
          { from: "m1", to: "c1" },
          { from: "c1", to: "c2", order: 2 },
          { from: "c2", to: "h2" },
          { from: "c1", to: "h1" },
          { from: "c2", to: "m2" },
        ],
      },
    ],
  },
  {
    id: "c3h6o3",
    category: "stereo",
    subtype: "Optical (enantiomers)",
    formula: "C₃H₆O₃",
    title: "(R)- vs. (S)-lactic acid",
    description:
      "Mirror-image molecules around a chiral carbon. Wedge = bond toward you, dash = bond away.",
    structures: [
      {
        label: "(R)-Lactic acid",
        name: "R configuration",
        molecule3dKey: "R-lactic-acid",
        atoms: [
          { id: "c", el: "C", x: 150, y: 90 },
          { id: "oh", el: "OH", x: 60, y: 90, },
          { id: "h", el: "H", x: 240, y: 90 },
          { id: "ch3", el: "CH₃", x: 150, y: 20 },
          { id: "cooh", el: "COOH", x: 150, y: 170 },
        ],
        bonds: [
          { from: "c", to: "oh", wedge: "up" },
          { from: "c", to: "h", wedge: "down" },
          { from: "c", to: "ch3" },
          { from: "c", to: "cooh" },
        ],
      },
      {
        label: "(S)-Lactic acid",
        name: "S configuration (mirror image)",
        molecule3dKey: "S-lactic-acid",
        atoms: [
          { id: "c", el: "C", x: 150, y: 90 },
          { id: "oh", el: "OH", x: 240, y: 90 },
          { id: "h", el: "H", x: 60, y: 90 },
          { id: "ch3", el: "CH₃", x: 150, y: 20 },
          { id: "cooh", el: "COOH", x: 150, y: 170 },
        ],
        bonds: [
          { from: "c", to: "oh", wedge: "up" },
          { from: "c", to: "h", wedge: "down" },
          { from: "c", to: "ch3" },
          { from: "c", to: "cooh" },
        ],
      },
    ],
  },
];

function StructureSVG({ structure }: { structure: IsomerStructure }) {
  const w = structure.width ?? 300;
  const h = structure.height ?? 180;
  const atomMap = new Map(structure.atoms.map((a) => [a.id, a]));

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      className="w-full h-auto"
      role="img"
      aria-label={`Structural diagram of ${structure.label}`}
    >
      {structure.bonds.map((b, i) => {
        const a = atomMap.get(b.from);
        const c = atomMap.get(b.to);
        if (!a || !c) return null;

        // Shorten line so it doesn't overlap atom labels
        const dx = c.x - a.x;
        const dy = c.y - a.y;
        const len = Math.hypot(dx, dy) || 1;
        const pad = 16;
        const ux = dx / len;
        const uy = dy / len;
        const x1 = a.x + ux * pad;
        const y1 = a.y + uy * pad;
        const x2 = c.x - ux * pad;
        const y2 = c.y - uy * pad;

        if (b.wedge === "up") {
          // Solid filled triangle (wedge toward viewer)
          const perpX = -uy * 5;
          const perpY = ux * 5;
          return (
            <polygon
              key={i}
              points={`${x1},${y1} ${x2 + perpX},${y2 + perpY} ${x2 - perpX},${y2 - perpY}`}
              fill="hsl(var(--foreground))"
            />
          );
        }
        if (b.wedge === "down") {
          // Dashed wedge (away from viewer)
          const dashes = 5;
          const lines = [];
          for (let d = 0; d < dashes; d++) {
            const t1 = d / dashes;
            const t2 = (d + 0.6) / dashes;
            const px1 = x1 + (x2 - x1) * t1;
            const py1 = y1 + (y2 - y1) * t1;
            const px2 = x1 + (x2 - x1) * t2;
            const py2 = y1 + (y2 - y1) * t2;
            const widen = 1 + t1 * 4;
            const perpX = -uy * widen;
            const perpY = ux * widen;
            lines.push(
              <line
                key={d}
                x1={px1 + perpX}
                y1={py1 + perpY}
                x2={px2 - perpX}
                y2={py2 - perpY}
                stroke="hsl(var(--foreground))"
                strokeWidth={1.5}
              />
            );
          }
          return <g key={i}>{lines}</g>;
        }

        if (b.order === 2) {
          const perpX = -uy * 3;
          const perpY = ux * 3;
          return (
            <g key={i} stroke="hsl(var(--foreground))" strokeWidth={1.5}>
              <line x1={x1 + perpX} y1={y1 + perpY} x2={x2 + perpX} y2={y2 + perpY} />
              <line x1={x1 - perpX} y1={y1 - perpY} x2={x2 - perpX} y2={y2 - perpY} />
            </g>
          );
        }
        if (b.order === 3) {
          const perpX = -uy * 4;
          const perpY = ux * 4;
          return (
            <g key={i} stroke="hsl(var(--foreground))" strokeWidth={1.5}>
              <line x1={x1} y1={y1} x2={x2} y2={y2} />
              <line x1={x1 + perpX} y1={y1 + perpY} x2={x2 + perpX} y2={y2 + perpY} />
              <line x1={x1 - perpX} y1={y1 - perpY} x2={x2 - perpX} y2={y2 - perpY} />
            </g>
          );
        }
        return (
          <line
            key={i}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            stroke="hsl(var(--foreground))"
            strokeWidth={1.5}
          />
        );
      })}

      {structure.atoms.map((a) => (
        <g key={a.id}>
          <rect
            x={a.x - a.el.length * 5 - 4}
            y={a.y - 11}
            width={a.el.length * 10 + 8}
            height={22}
            rx={4}
            fill="hsl(var(--background))"
          />
          <text
            x={a.x}
            y={a.y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize="13"
            fontFamily="ui-monospace, monospace"
            fill="hsl(var(--foreground))"
          >
            {a.el}
          </text>
        </g>
      ))}
    </svg>
  );
}

export default function IsomerExplorer() {
  const [selectedId, setSelectedId] = useState(isomerSets[0].id);
  const selected = isomerSets.find((s) => s.id === selectedId)!;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Isomer examples">
        {isomerSets.map((s) => (
          <Button
            key={s.id}
            variant={s.id === selectedId ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedId(s.id)}
            role="tab"
            aria-selected={s.id === selectedId}
          >
            <span className="font-mono mr-2">{s.formula}</span>
            <span className="hidden sm:inline">{s.subtype}</span>
          </Button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base">{selected.title}</CardTitle>
              <CardDescription className="mt-1">{selected.description}</CardDescription>
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              <Badge variant={selected.category === "stereo" ? "default" : "secondary"}>
                {selected.category === "stereo" ? "Stereoisomers" : "Structural"}
              </Badge>
              <Badge variant="outline" className="font-mono">{selected.formula}</Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selected.structures.map((st) => (
              <div
                key={st.label}
                className="border border-border rounded-md p-3 bg-muted/20 space-y-3"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-semibold text-foreground">{st.label}</p>
                  <p className="text-xs text-muted-foreground">{st.name}</p>
                </div>
                <StructureSVG structure={st} />
                {st.molecule3dKey && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">
                      3D · drag to rotate, scroll to zoom
                    </p>
                    <Suspense
                      fallback={
                        <div className="h-[240px] w-full rounded-md border border-border bg-muted/20 flex items-center justify-center text-xs text-muted-foreground">
                          Loading 3D…
                        </div>
                      }
                    >
                      <BallStickViewer
                        molecule={MOLECULES[st.molecule3dKey]}
                        ariaLabel={`Interactive 3D ball-and-stick model of ${st.label}`}
                      />
                    </Suspense>
                  </div>
                )}
              </div>
            ))}
          </div>
          {selected.id === "c3h6o3" && (
            <p className="mt-3 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Tip:</span> The two
              structures are non-superimposable mirror images. Try mentally
              flipping one across a vertical line through the central carbon.
            </p>
          )}
          {selected.id === "c4h8" && (
            <p className="mt-3 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Tip:</span> The C=C
              double bond cannot rotate freely, locking the methyl groups on the
              same side (cis) or opposite sides (trans).
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
